// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Inventory,
  AttachMoney,
  TrendingUp,
  Category,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Warning,
  ErrorOutline,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  getSupplyKPIs,
  SupplyKPIData,
  getRealYieldAnalytics,
  RealYieldData,
  RealYieldSupply,
  getEarlyReplacementAlerts,
  EarlyReplacementAlertsData,
  EarlyReplacementAlert,
  getDefectiveSupplyStatistics,
  DefectiveSupplyStatistics,
} from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const SupplyKPIs: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SupplyKPIData | null>(null);

  // Real Yield Analytics
  const [realYieldData, setRealYieldData] = useState<RealYieldData | null>(null);
  const [realYieldLoading, setRealYieldLoading] = useState(false);

  // Early Replacement Alerts
  const [alertsData, setAlertsData] = useState<EarlyReplacementAlertsData | null>(null);
  const [alertsLoading, setAlertsLoading] = useState(false);

  // Defective Supply Statistics
  const [defectiveData, setDefectiveData] = useState<DefectiveSupplyStatistics | null>(null);
  const [defectiveLoading, setDefectiveLoading] = useState(false);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [isOriginalFilter, setIsOriginalFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [tableOemFilter, setTableOemFilter] = useState<string>(''); // Local filter for table

  // Table sorting
  const [orderBy, setOrderBy] = useState<string>('stock_value');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Early Replacement Alerts sorting
  const [alertsOrderBy, setAlertsOrderBy] = useState<string>('dispatched_at');
  const [alertsOrder, setAlertsOrder] = useState<'asc' | 'desc'>('desc');

  // Expanded rows for Real Yield Analytics
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchData();
    fetchRealYieldData();
    fetchAlertsData();
    fetchDefectiveData();
  }, [categoryFilter, typeFilter, isOriginalFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (categoryFilter) params.category = categoryFilter;
      if (typeFilter) params.type = typeFilter;
      if (isOriginalFilter !== '') params.is_original = isOriginalFilter === 'true';

      const response = await getSupplyKPIs(params);
      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch supply KPIs');
      console.error('Error fetching supply KPIs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealYieldData = async () => {
    try {
      setRealYieldLoading(true);
      const params: { is_original?: boolean } = {};
      if (isOriginalFilter !== '') {
        params.is_original = isOriginalFilter === 'true';
      }
      const response = await getRealYieldAnalytics(params);
      setRealYieldData(response);
    } catch (err: any) {
      console.error('Error fetching real yield data:', err);
    } finally {
      setRealYieldLoading(false);
    }
  };

  const fetchAlertsData = async () => {
    try {
      setAlertsLoading(true);
      const response = await getEarlyReplacementAlerts({
        yield_threshold_pct: 70,
        days_lookback: 90,
      });
      setAlertsData(response);
    } catch (err: any) {
      console.error('Error fetching early replacement alerts:', err);
    } finally {
      setAlertsLoading(false);
    }
  };

  const fetchDefectiveData = async () => {
    try {
      setDefectiveLoading(true);
      const response = await getDefectiveSupplyStatistics({
        days_lookback: 90,
      });
      setDefectiveData(response);
    } catch (err: any) {
      console.error('Error fetching defective supply statistics:', err);
    } finally {
      setDefectiveLoading(false);
    }
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAlertsSort = (property: string) => {
    const isAsc = alertsOrderBy === property && alertsOrder === 'asc';
    setAlertsOrder(isAsc ? 'desc' : 'asc');
    setAlertsOrderBy(property);
  };

  // Sorted Early Replacement Alerts
  const sortedAlerts = React.useMemo(() => {
    if (!alertsData?.alerts) return [];

    return [...alertsData.alerts].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (alertsOrderBy) {
        case 'dispatched_at':
          aValue = new Date(a.dispatched_at).getTime();
          bValue = new Date(b.dispatched_at).getTime();
          break;
        case 'client_name':
          aValue = (a.client_name || '').toLowerCase();
          bValue = (b.client_name || '').toLowerCase();
          break;
        case 'serial_number':
          aValue = (a.serial_number || '').toLowerCase();
          bValue = (b.serial_number || '').toLowerCase();
          break;
        case 'sku':
          aValue = (a.sku || '').toLowerCase();
          bValue = (b.sku || '').toLowerCase();
          break;
        case 'yield_percentage':
          aValue = a.yield_percentage || 0;
          bValue = b.yield_percentage || 0;
          break;
        case 'real_yield':
          aValue = a.real_yield || 0;
          bValue = b.real_yield || 0;
          break;
        case 'waste_value':
          aValue = a.waste_value || 0;
          bValue = b.waste_value || 0;
          break;
        case 'days_in_service':
          aValue = a.days_in_service || 0;
          bValue = b.days_in_service || 0;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }

      if (alertsOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [alertsData?.alerts, alertsOrderBy, alertsOrder]);

  const sortedSupplies = React.useMemo(() => {
    if (!data?.supplies) return [];

    // First filter by search
    let filtered = data.supplies;
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(search) ||
        s.sku.toLowerCase().includes(search) ||
        (s.manufacturer && s.manufacturer.toLowerCase().includes(search))
      );
    }

    // Filter by OEM/Generic (local table filter)
    if (tableOemFilter !== '') {
      const isOem = tableOemFilter === 'true';
      filtered = filtered.filter(s => s.is_original === isOem);
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      let aValue = a[orderBy as keyof typeof a];
      let bValue = b[orderBy as keyof typeof b];

      if (aValue === null || aValue === undefined) aValue = 0;
      if (bValue === null || bValue === undefined) bValue = 0;

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [data?.supplies, orderBy, order, searchFilter, tableOemFilter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  // Prepare chart data
  const oemVsGenericData = [
    { name: 'OEM', value: data.summary.oem_stock_value, count: data.summary.oem_count },
    { name: 'Generic', value: data.summary.generic_stock_value, count: data.summary.generic_count },
  ];

  const categoryChartData = data.category_breakdown.slice(0, 10);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Supply KPIs & Analytics
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="toner">Toner</MenuItem>
                <MenuItem value="drum">Drum</MenuItem>
                <MenuItem value="fuser">Fuser Kit</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="part">Part</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="consumable">Consumable</MenuItem>
                <MenuItem value="part">Part</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>OEM / Generic</InputLabel>
              <Select
                value={isOriginalFilter}
                onChange={(e) => setIsOriginalFilter(e.target.value)}
                label="OEM / Generic"
              >
                <MenuItem value="">Both</MenuItem>
                <MenuItem value="true">OEM Only</MenuItem>
                <MenuItem value="false">Generic Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Supplies
                  </Typography>
                  <Typography variant="h4">
                    {data.summary.total_supplies}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    OEM: {data.summary.oem_count} | Generic: {data.summary.generic_count}
                  </Typography>
                </Box>
                <Inventory fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Stock Value
                  </Typography>
                  <Typography variant="h4">
                    ${data.summary.total_stock_value.toLocaleString()}
                  </Typography>
                </Box>
                <AttachMoney fontSize="large" color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg OEM Cost
                  </Typography>
                  <Typography variant="h4">
                    ${data.summary.oem_avg_unit_cost.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Stock Value: ${data.summary.oem_stock_value.toLocaleString()}
                  </Typography>
                </Box>
                <Category fontSize="large" color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Generic Cost
                  </Typography>
                  <Typography variant="h4">
                    ${data.summary.generic_avg_unit_cost.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    Savings: ${data.summary.cost_savings_potential.toFixed(2)}/unit
                  </Typography>
                </Box>
                <TrendingUp fontSize="large" color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                OEM vs Generic Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={oemVsGenericData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    label={(entry) => `${entry.name}: $${entry.value.toLocaleString()} (${entry.count})`}
                  >
                    {oemVsGenericData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Stock Value by Category
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="total_value" fill="#8884d8" name="Total Value" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Real Yield Analytics Section - Per Model Comparison */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Real Yield Analytics - OEM vs Generic by MFP Model
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Compares actual cost per 1K pages for OEM vs Generic supplies on each MFP model using EKM counter readings
          </Typography>

          {realYieldLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : realYieldData && realYieldData.models?.length > 0 ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {realYieldData.summary.total_models}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Models Analyzed
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {realYieldData.summary.models_with_both_types}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      With OEM & Generic Data
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4" color="success.dark">
                      {realYieldData.summary.models_generic_cheaper}
                    </Typography>
                    <Typography variant="body2">
                      Generic Cheaper
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                    <Typography variant="h4" color="primary.dark">
                      {realYieldData.summary.models_oem_cheaper}
                    </Typography>
                    <Typography variant="body2">
                      OEM Cheaper
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Per-Model Comparison Table with Expandable Rows */}
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={40} />
                      <TableCell>MFP Model</TableCell>
                      <TableCell align="center">OEM Cost/1K</TableCell>
                      <TableCell align="center">Generic Cost/1K</TableCell>
                      <TableCell align="center">Cheaper Option</TableCell>
                      <TableCell align="right">Savings/1K</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {realYieldData.models.map((model) => {
                      const isExpanded = expandedModels.has(model.model_id);
                      const toggleExpand = () => {
                        const newExpanded = new Set(expandedModels);
                        if (isExpanded) {
                          newExpanded.delete(model.model_id);
                        } else {
                          newExpanded.add(model.model_id);
                        }
                        setExpandedModels(newExpanded);
                      };

                      // Extract color from supply name (e.g., "Black", "Cyan", "Yellow", "Magenta")
                      const getColorFromName = (name: string): string => {
                        const lowerName = name.toLowerCase();
                        if (lowerName.includes('black') || lowerName.includes('negro')) return 'Black';
                        if (lowerName.includes('cyan') || lowerName.includes('cian')) return 'Cyan';
                        if (lowerName.includes('yellow') || lowerName.includes('amarillo')) return 'Yellow';
                        if (lowerName.includes('magenta')) return 'Magenta';
                        return 'Other';
                      };

                      // Group supplies by color and create OEM vs Generic comparisons
                      const colorComparisons: { [color: string]: { oem?: RealYieldSupply; generic?: RealYieldSupply } } = {};

                      model.oem.supplies.forEach(supply => {
                        const color = getColorFromName(supply.supply_name);
                        if (!colorComparisons[color]) colorComparisons[color] = {};
                        // If multiple OEM supplies for same color, pick the one with most samples
                        if (!colorComparisons[color].oem || supply.sample_count > colorComparisons[color].oem!.sample_count) {
                          colorComparisons[color].oem = supply;
                        }
                      });

                      model.generic.supplies.forEach(supply => {
                        const color = getColorFromName(supply.supply_name);
                        if (!colorComparisons[color]) colorComparisons[color] = {};
                        // If multiple Generic supplies for same color, pick the one with most samples
                        if (!colorComparisons[color].generic || supply.sample_count > colorComparisons[color].generic!.sample_count) {
                          colorComparisons[color].generic = supply;
                        }
                      });

                      // Sort colors: Black first, then CMYK order
                      const colorOrder = ['Black', 'Cyan', 'Magenta', 'Yellow', 'Other'];
                      const sortedColors = Object.keys(colorComparisons).sort(
                        (a, b) => colorOrder.indexOf(a) - colorOrder.indexOf(b)
                      );

                      return (
                        <React.Fragment key={model.model_id}>
                          <TableRow
                            hover
                            onClick={toggleExpand}
                            sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {model.manufacturer} {model.model_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {model.oem.supply_count} OEM, {model.generic.supply_count} Generic supplies
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {model.oem.cost_per_1k_pages > 0 ? (
                                <Box>
                                  <Typography variant="body2" fontWeight="medium" color="primary">
                                    ${model.oem.cost_per_1k_pages.toFixed(2)}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {model.oem.total_samples} samples
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {model.generic.cost_per_1k_pages > 0 ? (
                                <Box>
                                  <Typography variant="body2" fontWeight="medium" color="success.main">
                                    ${model.generic.cost_per_1k_pages.toFixed(2)}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {model.generic.total_samples} samples
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={model.comparison.cheaper_option}
                                size="small"
                                color={
                                  model.comparison.cheaper_option === 'Generic' ? 'success' :
                                  model.comparison.cheaper_option === 'OEM' ? 'primary' :
                                  'default'
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {model.comparison.has_both_types ? (
                                <Typography variant="body2" color="success.main" fontWeight="medium">
                                  ${model.comparison.savings_per_1k.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Color-by-Color Comparison
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell>Color</TableCell>
                                        <TableCell>OEM Supply</TableCell>
                                        <TableCell align="right">OEM Cost/1K</TableCell>
                                        <TableCell>Generic Supply</TableCell>
                                        <TableCell align="right">Gen Cost/1K</TableCell>
                                        <TableCell align="center">Winner</TableCell>
                                        <TableCell align="right">Savings/1K</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {sortedColors.map(color => {
                                        const comparison = colorComparisons[color];
                                        const oemCost = comparison.oem?.cost_per_1k_pages || 0;
                                        const genCost = comparison.generic?.cost_per_1k_pages || 0;
                                        const hasBoth = oemCost > 0 && genCost > 0;
                                        const winner = hasBoth ? (oemCost < genCost ? 'OEM' : 'Generic') : (oemCost > 0 ? 'OEM only' : 'Generic only');
                                        const savings = hasBoth ? Math.abs(oemCost - genCost) : 0;

                                        return (
                                          <TableRow key={color} hover>
                                            <TableCell>
                                              <Chip
                                                label={color}
                                                size="small"
                                                sx={{
                                                  bgcolor: color === 'Black' ? '#333' :
                                                           color === 'Cyan' ? '#00bcd4' :
                                                           color === 'Magenta' ? '#e91e63' :
                                                           color === 'Yellow' ? '#ffeb3b' : '#9e9e9e',
                                                  color: color === 'Yellow' ? '#000' : '#fff',
                                                  fontWeight: 'bold',
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {comparison.oem ? (
                                                <Box>
                                                  <Typography variant="caption" display="block">
                                                    {comparison.oem.sku}
                                                  </Typography>
                                                  <Typography variant="caption" color="textSecondary">
                                                    {comparison.oem.sample_count} samples
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Typography variant="caption" color="textSecondary">-</Typography>
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {oemCost > 0 ? (
                                                <Typography variant="body2" fontWeight={hasBoth && oemCost < genCost ? 'bold' : 'normal'} color={hasBoth && oemCost < genCost ? 'success.main' : 'inherit'}>
                                                  ${oemCost.toFixed(2)}
                                                </Typography>
                                              ) : (
                                                <Typography variant="body2" color="textSecondary">-</Typography>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {comparison.generic ? (
                                                <Box>
                                                  <Typography variant="caption" display="block">
                                                    {comparison.generic.sku}
                                                  </Typography>
                                                  <Typography variant="caption" color="textSecondary">
                                                    {comparison.generic.sample_count} samples
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Typography variant="caption" color="textSecondary">-</Typography>
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {genCost > 0 ? (
                                                <Typography variant="body2" fontWeight={hasBoth && genCost < oemCost ? 'bold' : 'normal'} color={hasBoth && genCost < oemCost ? 'success.main' : 'inherit'}>
                                                  ${genCost.toFixed(2)}
                                                </Typography>
                                              ) : (
                                                <Typography variant="body2" color="textSecondary">-</Typography>
                                              )}
                                            </TableCell>
                                            <TableCell align="center">
                                              <Chip
                                                label={winner}
                                                size="small"
                                                color={winner === 'Generic' ? 'success' : winner === 'OEM' ? 'primary' : 'default'}
                                                variant={hasBoth ? 'filled' : 'outlined'}
                                              />
                                            </TableCell>
                                            <TableCell align="right">
                                              {hasBoth ? (
                                                <Typography variant="body2" color="success.main" fontWeight="medium">
                                                  ${savings.toFixed(2)}
                                                </Typography>
                                              ) : (
                                                <Typography variant="body2" color="textSecondary">-</Typography>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info">No real yield data available. Need at least 2 consecutive toner dispatches per supply with EKM counter readings.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Early Replacement Alerts Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Warning color="warning" />
            <Typography variant="h6">
              Early Replacement Alerts
            </Typography>
            {alertsData && alertsData.summary.total_alerts > 0 && (
              <Chip
                label={`${alertsData.summary.total_alerts} Alerts`}
                color="error"
                size="small"
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Supplies replaced before reaching 70% of advertised yield (last 90 days). Shows potential waste from premature replacements.
          </Typography>

          {alertsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : alertsData && alertsData.alerts.length > 0 ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {alertsData.summary.total_alerts}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Alerts
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                    <Typography variant="h4" color="error.dark">
                      ${alertsData.summary.total_waste_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                    <Typography variant="body2">
                      Total Waste Value
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="textSecondary">
                      {alertsData.summary.days_lookback}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Days Lookback
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Alerts Table */}
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={alertsOrderBy === 'dispatched_at'}
                          direction={alertsOrderBy === 'dispatched_at' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('dispatched_at')}
                        >
                          Dispatched
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={alertsOrderBy === 'client_name'}
                          direction={alertsOrderBy === 'client_name' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('client_name')}
                        >
                          Client
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={alertsOrderBy === 'serial_number'}
                          direction={alertsOrderBy === 'serial_number' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('serial_number')}
                        >
                          Equipment
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={alertsOrderBy === 'sku'}
                          direction={alertsOrderBy === 'sku' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('sku')}
                        >
                          Supply
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={alertsOrderBy === 'yield_percentage'}
                          direction={alertsOrderBy === 'yield_percentage' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('yield_percentage')}
                        >
                          Yield %
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={alertsOrderBy === 'real_yield'}
                          direction={alertsOrderBy === 'real_yield' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('real_yield')}
                        >
                          Real / Advertised
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={alertsOrderBy === 'waste_value'}
                          direction={alertsOrderBy === 'waste_value' ? alertsOrder : 'asc'}
                          onClick={() => handleAlertsSort('waste_value')}
                        >
                          Waste Value
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAlerts.map((alert) => (
                      <TableRow
                        key={alert.dispatch_id}
                        hover
                        sx={{
                          bgcolor: alert.yield_percentage < 30 ? 'error.lighter' : undefined,
                          '&:hover': {
                            bgcolor: alert.yield_percentage < 30 ? 'error.light' : undefined,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateForDisplay(alert.dispatched_at)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {alert.days_in_service?.toFixed(0) || '?'} days
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{alert.client_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{alert.serial_number}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {alert.manufacturer} {alert.model_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{alert.sku}</Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="caption" color="textSecondary">
                              {alert.supply_name.substring(0, 30)}
                            </Typography>
                            <Chip
                              label={alert.is_original ? 'OEM' : 'Generic'}
                              size="small"
                              color={alert.is_original ? 'primary' : 'success'}
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              alert.yield_percentage < 30
                                ? 'error.main'
                                : alert.yield_percentage < 50
                                ? 'warning.main'
                                : 'textPrimary'
                            }
                          >
                            {alert.yield_percentage.toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {alert.real_yield.toLocaleString()} / {alert.advertised_yield.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="error.main"
                          >
                            ${alert.waste_value.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="success">
              No early replacement alerts in the last 90 days. All supplies were used to at least 70% yield before replacement.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Defective Supply Statistics Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ErrorOutline color="error" />
            <Typography variant="h6">
              Suministros Defectuosos
            </Typography>
            {defectiveData && defectiveData.summary.total_defective_requests > 0 && (
              <Chip
                label={`${defectiveData.summary.total_defective_requests} Reportados`}
                color="error"
                size="small"
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Suministros marcados como defectuosos durante reemplazos anticipados (ultimos 90 dias).
            Ayuda a identificar problemas de calidad por SKU o fabricante.
          </Typography>

          {defectiveLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : defectiveData && defectiveData.summary.total_defective_requests > 0 ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                    <Typography variant="h4" color="error.dark">
                      {defectiveData.summary.total_defective_requests}
                    </Typography>
                    <Typography variant="body2">
                      Total Defectuosos
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {defectiveData.summary.defect_rate_pct}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tasa de Defectos
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ({defectiveData.summary.total_defective_requests} de {defectiveData.summary.total_early_replacements} reemplazos)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      ${defectiveData.summary.total_defective_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Costo Total Defectuosos
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="textSecondary">
                      {defectiveData.summary.days_lookback}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Dias Analizados
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Two columns: By SKU and By Manufacturer */}
              <Grid container spacing={3}>
                {/* By SKU */}
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Por SKU (Top 20)
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>SKU</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell align="center">Tipo</TableCell>
                          <TableCell align="right">Defectos</TableCell>
                          <TableCell align="right">Costo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {defectiveData.by_supply.map((item) => (
                          <TableRow key={item.sku} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.sku}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                {item.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {item.manufacturer}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.is_original ? 'OEM' : 'Generico'}
                                size="small"
                                color={item.is_original ? 'primary' : 'success'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                {item.defect_count}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({item.total_quantity} uds)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                ${item.total_cost.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* By Manufacturer */}
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Por Fabricante
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Fabricante</TableCell>
                          <TableCell align="right">Defectos</TableCell>
                          <TableCell align="right">Costo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {defectiveData.by_manufacturer.map((item) => (
                          <TableRow key={item.manufacturer} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.manufacturer}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                {item.defect_count}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({item.total_quantity} uds)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                ${item.total_cost.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              {/* Recent Defective Requests */}
              {defectiveData.recent_defective.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Solicitudes Recientes con Defectos
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Requisicion</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>Razon</TableCell>
                          <TableCell>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {defectiveData.recent_defective.slice(0, 20).map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDateForDisplay(item.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.requisition_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.client_name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.sku}</Typography>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 100 }}>
                                  {item.supply_name.substring(0, 20)}...
                                </Typography>
                                <Chip
                                  label={item.is_original ? 'OEM' : 'Gen'}
                                  size="small"
                                  color={item.is_original ? 'primary' : 'success'}
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                                {item.early_replacement_reason || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.status}
                                size="small"
                                color={item.status === 'dispatched' ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="success">
              No se han reportado suministros defectuosos en los ultimos 90 dias.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Supply Details Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Supply Details
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>OEM/Generic</InputLabel>
                <Select
                  value={tableOemFilter}
                  label="OEM/Generic"
                  onChange={(e) => setTableOemFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">OEM Only</MenuItem>
                  <MenuItem value="false">Generic Only</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search by Name, SKU or Manufacturer"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="e.g. HP, W9050MC, toner..."
                sx={{ width: 350 }}
              />
            </Box>
          </Box>
          {data.note && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {data.note}
            </Alert>
          )}
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'sku'}
                      direction={orderBy === 'sku' ? order : 'asc'}
                      onClick={() => handleSort('sku')}
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'stock_quantity'}
                      direction={orderBy === 'stock_quantity' ? order : 'asc'}
                      onClick={() => handleSort('stock_quantity')}
                    >
                      Stock
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'unit_cost'}
                      direction={orderBy === 'unit_cost' ? order : 'asc'}
                      onClick={() => handleSort('unit_cost')}
                    >
                      Unit Cost
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'stock_value'}
                      direction={orderBy === 'stock_value' ? order : 'asc'}
                      onClick={() => handleSort('stock_value')}
                    >
                      Stock Value
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'factory_yield'}
                      direction={orderBy === 'factory_yield' ? order : 'asc'}
                      onClick={() => handleSort('factory_yield')}
                    >
                      Factory Yield
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'real_yield'}
                      direction={orderBy === 'real_yield' ? order : 'asc'}
                      onClick={() => handleSort('real_yield')}
                    >
                      Real Yield
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Cost/Page Factory</TableCell>
                  <TableCell align="right">Cost/Page Real</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSupplies.map((supply) => (
                  <TableRow key={supply.id} hover>
                    <TableCell>{supply.sku}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {supply.name}
                        {supply.is_original ? (
                          <Chip label="OEM" size="small" color="primary" />
                        ) : (
                          <Chip label="Generic" size="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{supply.type}</TableCell>
                    <TableCell align="right">{supply.stock_quantity}</TableCell>
                    <TableCell align="right">${supply.unit_cost.toFixed(2)}</TableCell>
                    <TableCell align="right">${supply.stock_value.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {supply.factory_yield ? supply.factory_yield.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {supply.real_yield ? (
                        <Box>
                          <Typography variant="body2">
                            {supply.real_yield.toLocaleString()}
                          </Typography>
                          {supply.real_yield_samples && (
                            <Typography variant="caption" color="textSecondary">
                              ({supply.real_yield_samples} samples)
                            </Typography>
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {supply.cost_per_page_factory
                        ? `$${supply.cost_per_page_factory.toFixed(4)}`
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {supply.cost_per_page_real ? (
                        <Typography
                          variant="body2"
                          color={
                            supply.cost_per_page_factory && supply.cost_per_page_real < supply.cost_per_page_factory
                              ? 'success.main'
                              : supply.cost_per_page_factory && supply.cost_per_page_real > supply.cost_per_page_factory
                              ? 'error.main'
                              : 'textPrimary'
                          }
                        >
                          ${supply.cost_per_page_real.toFixed(4)}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SupplyKPIs;
